import { useEffect, useState } from 'react';
import type {
  CategoriaGasto,
  EditarGasto,
  EditarHoras,
  GastoPropiedad,
  TipoHora,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { incidenciaActiva } from '../componentes.js';
import { minutosDeTexto } from '../pages/CargaRapida.js';
import { Icono } from './Iconos.js';
import { Modal } from './Modal.js';

/**
 * CORREGIR Y QUITAR UNA CARGA.
 *
 * Se teclea mal —seis horas donde eran dos, un cero de más en un importe, el
 * gasto en la casa equivocada— y hasta ahora la carga se quedaba dentro para
 * siempre: contando en el resumen del mes, en lo que se le factura al cliente y
 * en lo que se le paga a alguien. La única salida era dejar de mirar los
 * números, que es peor que no tenerlos.
 *
 * SOLO DIRECCIÓN, y no porque lo diga esta pantalla: lo exige el servidor. Una
 * carga de horas es lo que alguien va a cobrar, y que cualquiera pueda cambiar
 * las de otro convierte la nómina en una conversación.
 *
 * NO SE PUEDE CAMBIAR DE QUIÉN SON LAS HORAS. Corregir la fecha o el rato es
 * arreglar un dato; cambiar la persona es mover dinero de un bolsillo a otro, y
 * eso se hace quitando la carga y volviéndola a poner, que deja dos apuntes en
 * la auditoría en vez de uno ambiguo.
 */

const CATEGORIAS: CategoriaGasto[] = [
  'PROVEEDORES_CLIENTES',
  'VEHICULOS',
  'LIMPIEZA',
  'OFICINA',
  'MARKETING',
  'PROPERTY_MANAGEMENT',
  'SERVICIOS',
  'RECUPERABLES',
  'GESTORIA',
  'EQUIPO',
  'SOFTWARE',
  'REAL_ESTATE',
  'GASTOS_GENERALES',
  'OTROS_DIRECTOS',
  'OTRO',
];

const TRABAJOS: TipoHora[] = [
  'GESTION',
  'MANTENIMIENTO',
  'LIMPIEZA',
  'OBRA',
  'ADMINISTRACION',
  'COMERCIAL',
  'DESPLAZAMIENTO',
  'OTRO',
];

/** «1,45» o «1.45» → céntimos. Null si no se entiende. */
function aCents(bruto: string): number | null {
  const n = Number(bruto.trim().replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

function aMinutos(bruto: string): number | null {
  return minutosDeTexto(bruto);
}

export interface GastoEditable {
  id: string;
  fecha: string;
  concepto: string;
  categoria: CategoriaGasto;
  costeCents: number;
  beneficiario: string | null;
  /** Sólo los gastos de una casa llevan margen: un gasto interno no se repercute. */
  margenPb?: number;
  descripcion?: string | null;
  incidencias?: GastoPropiedad['incidencias'];
  /** Si sale de una carga de horas: su dinero se corrige en la carga. */
  horas?: GastoPropiedad['horas'];
}

type IncidenciaElegible = { id: string; codigo: string; titulo: string };

/**
 * Las incidencias que se pueden citar: las abiertas de la casa y, aunque ya
 * esten cerradas, las que el gasto ya citaba (si no, guardar las borraria).
 */
function ElegirIncidencias({
  idioma,
  propiedadId,
  citadas,
  elegidas,
  onCambio,
}: {
  idioma: Idioma;
  propiedadId: string;
  citadas: IncidenciaElegible[];
  elegidas: string[];
  onCambio: (ids: string[]) => void;
}) {
  const [abiertas, setAbiertas] = useState<IncidenciaElegible[]>([]);
  useEffect(() => {
    api
      .incidencias(propiedadId)
      .then((r) =>
        setAbiertas(
          r.incidencias
            .filter((i) => incidenciaActiva(i.estado))
            .map((i) => ({ id: i.id, codigo: i.codigo, titulo: i.titulo })),
        ),
      )
      .catch(() => setAbiertas([]));
  }, [propiedadId]);
  const lista = [...citadas, ...abiertas.filter((a) => !citadas.some((c) => c.id === a.id))];
  if (lista.length === 0) {
    return <p className="suave">{t(idioma, 'gastos.sinIncidenciasAbiertas')}</p>;
  }
  return (
    <div className="lista-marcable">
      {lista.map((i) => (
        <label key={i.id} className="marcable-linea">
          <input
            type="checkbox"
            checked={elegidas.includes(i.id)}
            onChange={(e) =>
              onCambio(e.target.checked ? [...elegidas, i.id] : elegidas.filter((x) => x !== i.id))
            }
          />
          <span>
            <b>{i.codigo}</b> – {i.titulo}
          </span>
        </label>
      ))}
    </div>
  );
}

export function ModalEditarGasto({
  idioma,
  gasto,
  propiedadId,
  onGuardado,
  onCerrar,
}: {
  idioma: Idioma;
  gasto: GastoEditable;
  /** La casa del gasto: con ella se pueden citar sus incidencias. */
  propiedadId?: string;
  onGuardado: () => void;
  onCerrar: () => void;
}) {
  const deHoras = gasto.horas ?? null;
  const [descripcion, setDescripcion] = useState(gasto.descripcion ?? '');
  const [incidencias, setIncidencias] = useState<string[]>(
    (gasto.incidencias ?? []).map((i) => i.id),
  );
  const [horasTexto, setHorasTexto] = useState(
    deHoras ? String(Math.round((deHoras.minutos / 60) * 100) / 100).replace('.', ',') : '',
  );
  const [costeHora, setCosteHora] = useState(
    deHoras ? String(deHoras.costeHoraCents / 100).replace('.', ',') : '',
  );
  const [precioHora, setPrecioHora] = useState(
    deHoras ? String(deHoras.precioHoraClienteCents / 100).replace('.', ',') : '',
  );
  const [extra, setExtra] = useState(deHoras?.facturableExtra ?? false);
  const [fecha, setFecha] = useState(gasto.fecha.slice(0, 10));
  const [concepto, setConcepto] = useState(gasto.concepto);
  const [categoria, setCategoria] = useState<CategoriaGasto>(gasto.categoria);
  const [importe, setImporte] = useState(String(gasto.costeCents / 100));
  const [beneficiario, setBeneficiario] = useState(gasto.beneficiario ?? '');
  const [margen, setMargen] = useState(
    gasto.margenPb === undefined ? '' : String(gasto.margenPb / 100),
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
    UNA LINEA QUE SALE DE HORAS se corrige en su carga: horas, coste y precio
    por hora, y si se cobra aparte. La linea se rehace sola con eso; tocar su
    importe a mano la dejaria diciendo otra cosa que la carga.
  */
  async function guardarHoras(h: NonNullable<GastoEditable['horas']>) {
    const minutos = aMinutos(horasTexto);
    const coste = aCents(costeHora);
    const precio = precioHora.trim() === '' ? 0 : aCents(precioHora);
    if (minutos === null) {
      setError(t(idioma, 'finreg.indicaHoras'));
      return;
    }
    if (coste === null || precio === null) {
      setError(t(idioma, 'finreg.indicaImporte'));
      return;
    }
    setError(null);
    setGuardando(true);
    try {
      await api.finEditarHoras(h.timeEntryId, {
        fecha,
        minutos,
        costeHoraCents: coste,
        precioHoraClienteCents: precio,
        facturableExtra: extra,
        descripcion: descripcion.trim() || null,
        incidencias,
      });
      onGuardado();
      onCerrar();
    } catch (e) {
      setError(e instanceof ErrorApi && e.message ? e.message : t(idioma, 'carga.noSeGuardo'));
      setGuardando(false);
    }
  }

  async function guardar() {
    if (deHoras) {
      await guardarHoras(deHoras);
      return;
    }
    const cents = aCents(importe);
    if (cents === null || concepto.trim().length < 2) {
      setError(t(idioma, 'finreg.indicaGasto'));
      return;
    }
    setError(null);
    setGuardando(true);
    const margenPb = margen.trim() === '' ? undefined : aCents(margen);
    const cambios: EditarGasto = {
      fecha,
      concepto: concepto.trim(),
      categoria,
      costeCents: cents,
      beneficiario: beneficiario.trim() || null,
      descripcion: descripcion.trim() || null,
      ...(propiedadId ? { incidencias } : {}),
      ...(gasto.margenPb !== undefined && margenPb !== null && margenPb !== undefined
        ? { margenPb }
        : {}),
    };
    try {
      await api.finEditarGasto(gasto.id, cambios);
      onGuardado();
      onCerrar();
    } catch (e) {
      setError(e instanceof ErrorApi && e.message ? e.message : t(idioma, 'carga.noSeGuardo'));
      setGuardando(false);
    }
  }

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'carga.editarGasto')} icono="billete" onCerrar={onCerrar}>
      <div className="formulario" style={{ maxWidth: 'none' }}>
        <div className="campo">
          <label htmlFor="ec-fecha">{t(idioma, 'interno.fecha')}</label>
          <input id="ec-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </div>
        {deHoras ? (
          <>
            <p className="suave">{gasto.concepto}</p>
            <div className="fin-form-fila">
              <div className="campo">
                <label htmlFor="ec-horas">{t(idioma, 'carga.horas')}</label>
                <input
                  id="ec-horas"
                  inputMode="decimal"
                  value={horasTexto}
                  onChange={(e) => setHorasTexto(e.target.value)}
                />
              </div>
              <div className="campo">
                <label htmlFor="ec-coste-hora">{t(idioma, 'rapido.costeHora')}</label>
                <input
                  id="ec-coste-hora"
                  inputMode="decimal"
                  value={costeHora}
                  onChange={(e) => setCosteHora(e.target.value)}
                />
              </div>
              <div className="campo">
                <label htmlFor="ec-precio-hora">{t(idioma, 'rapido.precioHora')}</label>
                <input
                  id="ec-precio-hora"
                  inputMode="decimal"
                  value={precioHora}
                  onChange={(e) => setPrecioHora(e.target.value)}
                />
              </div>
            </div>
            <label className="marcable-linea">
              <input type="checkbox" checked={extra} onChange={(e) => setExtra(e.target.checked)} />
              <span>
                <b>{t(idioma, 'rapido.facturableExtra')}</b>{' '}
                <span className="suave">{t(idioma, extra ? 'rapido.extraSi' : 'rapido.extraNo')}</span>
              </span>
            </label>
          </>
        ) : (
          <div className="campo">
            <label htmlFor="ec-concepto">{t(idioma, 'interno.concepto')}</label>
            <input
              id="ec-concepto"
              type="text"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
            />
          </div>
        )}
        <div className="campo">
          <label htmlFor="ec-descripcion">{t(idioma, 'gastos.descripcion')}</label>
          <textarea
            id="ec-descripcion"
            rows={3}
            maxLength={2000}
            value={descripcion}
            placeholder={t(idioma, 'gastos.descripcionPlaceholder')}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </div>
        {propiedadId && (
          <fieldset className="campo">
            <legend>{t(idioma, 'gastos.incidencias')}</legend>
            <ElegirIncidencias
              idioma={idioma}
              propiedadId={propiedadId}
              citadas={gasto.incidencias ?? []}
              elegidas={incidencias}
              onCambio={setIncidencias}
            />
          </fieldset>
        )}
        {!deHoras && (
        <>
        <div className="campo">
          <label htmlFor="ec-categoria">{t(idioma, 'interno.categoria')}</label>
          <select
            id="ec-categoria"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value as CategoriaGasto)}
          >
            {CATEGORIAS.map((c) => (
              <option key={c} value={c}>
                {t(idioma, `categoriaGasto.${c}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="ec-importe">{t(idioma, 'interno.importe')}</label>
          <input
            id="ec-importe"
            inputMode="decimal"
            value={importe}
            onChange={(e) => setImporte(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="ec-benef">{t(idioma, 'interno.beneficiario')}</label>
          <input
            id="ec-benef"
            type="text"
            value={beneficiario}
            onChange={(e) => setBeneficiario(e.target.value)}
          />
        </div>
        {gasto.margenPb !== undefined && (
          <div className="campo">
            <label htmlFor="ec-margen">{t(idioma, 'finreg.margen')}</label>
            <input
              id="ec-margen"
              inputMode="decimal"
              value={margen}
              onChange={(e) => setMargen(e.target.value)}
            />
          </div>
        )}
        </>
        )}

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="acciones">
          <button type="button" className="secundario" onClick={onCerrar}>
            {t(idioma, 'app.cancelar')}
          </button>
          <button type="button" disabled={guardando} onClick={() => void guardar()}>
            {t(idioma, 'carga.guardar')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export interface HorasEditables {
  id: string;
  fecha: string;
  minutos: number;
  tipo: TipoHora;
  descripcion: string | null;
  usuarioNombre: string;
  /** La casa: sin casa no hay cliente al que cobrarle nada aparte. */
  propiedadId?: string | null;
  facturableExtra?: boolean;
}

export function ModalEditarHoras({
  idioma,
  hora,
  onGuardado,
  onCerrar,
}: {
  idioma: Idioma;
  hora: HorasEditables;
  onGuardado: () => void;
  onCerrar: () => void;
}) {
  const [fecha, setFecha] = useState(hora.fecha.slice(0, 10));
  const [horas, setHoras] = useState(String(Math.round((hora.minutos / 60) * 100) / 100));
  const [tipo, setTipo] = useState<TipoHora>(hora.tipo);
  const [descripcion, setDescripcion] = useState(hora.descripcion ?? '');
  const [extra, setExtra] = useState(hora.facturableExtra === true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function guardar() {
    const minutos = aMinutos(horas);
    if (minutos === null) {
      setError(t(idioma, 'finreg.indicaHoras'));
      return;
    }
    setError(null);
    setGuardando(true);
    const cambios: EditarHoras = {
      fecha,
      minutos,
      tipo,
      descripcion: descripcion.trim() || null,
      ...(hora.propiedadId ? { facturableExtra: extra } : {}),
    };
    try {
      await api.finEditarHoras(hora.id, cambios);
      onGuardado();
      onCerrar();
    } catch (e) {
      setError(e instanceof ErrorApi && e.message ? e.message : t(idioma, 'carga.noSeGuardo'));
      setGuardando(false);
    }
  }

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'carga.editarHoras')} icono="reloj" onCerrar={onCerrar}>
      <div className="formulario" style={{ maxWidth: 'none' }}>
        {/* De quién son no se toca aquí: eso es mover dinero, no corregir un dato. */}
        <p className="suave">{hora.usuarioNombre}</p>
        <div className="campo">
          <label htmlFor="eh-fecha">{t(idioma, 'interno.fecha')}</label>
          <input id="eh-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="eh-horas">{t(idioma, 'carga.horas')}</label>
          <input
            id="eh-horas"
            inputMode="decimal"
            value={horas}
            onChange={(e) => setHoras(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="eh-tipo">{t(idioma, 'interno.tipo')}</label>
          <select id="eh-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoHora)}>
            {TRABAJOS.map((x) => (
              <option key={x} value={x}>
                {t(idioma, `tipoHora.${x}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="eh-desc">{t(idioma, 'interno.descripcion')}</label>
          <input
            id="eh-desc"
            type="text"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </div>
        {/*
          Marcar unas horas como extra es lo que hace que se paguen encima de la
          cuota fija y se le cobren al cliente. Las cargas hechas antes de que
          existiera esta pregunta se corrigen aqui.
        */}
        {hora.propiedadId && (
          <label className="marcable-linea">
            <input type="checkbox" checked={extra} onChange={(e) => setExtra(e.target.checked)} />
            <span>
              <b>{t(idioma, 'rapido.facturableExtra')}</b>{' '}
              <span className="suave">{t(idioma, extra ? 'rapido.extraSi' : 'rapido.extraNo')}</span>
            </span>
          </label>
        )}

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="acciones">
          <button type="button" className="secundario" onClick={onCerrar}>
            {t(idioma, 'app.cancelar')}
          </button>
          <button type="button" disabled={guardando} onClick={() => void guardar()}>
            {t(idioma, 'carga.guardar')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Los dos botones de la fila.
 *
 * Se paran ahí mismo (`stopPropagation`) porque viven dentro de filas que ya
 * hacen algo al pulsarlas —abrir la factura del gasto—, y quien va a corregir
 * un importe no quiere que además se le abra un diálogo encima.
 */
export function AccionesCarga({
  idioma,
  onEditar,
  onEliminar,
}: {
  idioma: Idioma;
  onEditar: () => void;
  onEliminar: () => void;
}) {
  return (
    <span className="acciones-carga" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="icono-accion"
        title={t(idioma, 'carga.editar')}
        aria-label={t(idioma, 'carga.editar')}
        onClick={onEditar}
      >
        <Icono nombre="lapiz" />
      </button>
      <button
        type="button"
        className="icono-accion peligro"
        title={t(idioma, 'carga.eliminar')}
        aria-label={t(idioma, 'carga.eliminar')}
        onClick={onEliminar}
      >
        <Icono nombre="papelera" />
      </button>
    </span>
  );
}
